/**
 * Client-side allow-list for leave document uploads.
 *
 * Mirrors the server's `ALLOWED_MIME_TYPES` (services/api/src/storage/s3.py):
 * JPEG, PNG, WebP, PDF. The server is the real gate (it sniffs magic bytes and
 * returns 415), but re-checking on the client keeps unsupported files — most
 * commonly an iPhone `.HEIC` — out of the staged list entirely, so the user
 * gets an instant, obvious reason instead of a failed upload round-trip.
 */

/** MIME types the upload route accepts. */
export const ACCEPTED_UPLOAD_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
] as const

/** Value for a file input's `accept` attribute — a picker-level hint only. */
export const UPLOAD_ACCEPT_ATTR = ACCEPTED_UPLOAD_MIME_TYPES.join(',')

/** Extensions mapping to the accepted MIME types — the fallback when a browser
 *  reports an empty or unrecognised `file.type` for a supported file. */
const ACCEPTED_UPLOAD_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.pdf']

/** Shared reason shown when a file is rejected for its type (client + 415). */
export const UNSUPPORTED_UPLOAD_MESSAGE =
  'Unsupported file type. Use JPEG, PNG, WEBP, or PDF.'

/**
 * True when a picked file is one the server will accept.
 *
 * Checks the reported MIME type first; falls back to the extension when the
 * browser gives no / an unrecognised type. A `.heic`/`.heif` (reported as
 * `image/heic` or with no type) matches neither and is rejected.
 */
export function isAcceptedUploadFile(file: File): boolean {
  const type = file.type.toLowerCase()
  if (type && (ACCEPTED_UPLOAD_MIME_TYPES as readonly string[]).includes(type)) {
    return true
  }
  const name = file.name.toLowerCase()
  return ACCEPTED_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext))
}
